import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, LinearProgress, IconButton, Badge, Popover, Tooltip } from '@mui/material';
import {
    CheckCircle as CompleteIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';

const STATUS_ICONS = {
    pending: PendingIcon,
    extracting_text: SyncIcon,
    analyzing_structure: SyncIcon,
    extracting_rates: SyncIcon,
    validating: SyncIcon,
    complete: CompleteIcon,
    failed: ErrorIcon
};

const STATUS_COLORS = {
    pending: '#888',
    extracting_text: '#b28c50',
    analyzing_structure: '#b28c50',
    extracting_rates: '#b28c50',
    validating: '#00ff41',
    complete: '#00ff41',
    failed: '#e74c3c'
};

export default function GlobalBackgroundTasks({ isRetro }) {
    const { currentUser } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const pollingRef = useRef(null);
    const prevActiveCountRef = useRef(0);

    const fetchJobs = useCallback(async () => {
        if (!currentUser) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/v1/extract/jobs?limit=5`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                const newJobs = data.jobs || [];
                setJobs(newJobs);

                const activeCount = newJobs.filter(j =>
                    j.status !== 'complete' && j.status !== 'failed'
                ).length;
                prevActiveCountRef.current = activeCount;
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
        }
    }, [currentUser]);

    // Polling logic
    useEffect(() => {
        if (!currentUser) return;

        const activeJobs = jobs.filter(j =>
            j.status !== 'complete' && j.status !== 'failed'
        );

        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        // Poll faster when active, slower when idle
        const pollInterval = activeJobs.length > 0 ? 3000 : 30000;
        pollingRef.current = setInterval(fetchJobs, pollInterval);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [jobs, currentUser, fetchJobs]);

    // Initial fetch
    useEffect(() => {
        if (currentUser) {
            fetchJobs();
        }
    }, [currentUser, fetchJobs]);

    const activeJobs = jobs.filter(j => j.status !== 'complete' && j.status !== 'failed');
    const recentJobs = jobs.filter(j => j.status === 'complete' || j.status === 'failed').slice(0, 3);
    const open = Boolean(anchorEl);

    // Don't render if no jobs
    if (jobs.length === 0) {
        return null;
    }

    return (
        <>
            <Tooltip title={activeJobs.length > 0 ? `${activeJobs.length} task${activeJobs.length > 1 ? 's' : ''} running` : 'Background tasks'}>
                <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        color: activeJobs.length > 0
                            ? (isRetro ? vortxColors.gold : vortxColors.white)
                            : (isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.7)'),
                        mr: 1,
                        border: isRetro && activeJobs.length > 0 ? `1px solid ${vortxColors.gold}` : 'none',
                        '&:hover': {
                            bgcolor: isRetro ? 'rgba(178, 140, 80, 0.2)' : 'rgba(255, 255, 255, 0.2)'
                        }
                    }}
                >
                    <Badge
                        badgeContent={activeJobs.length}
                        color="warning"
                        invisible={activeJobs.length === 0}
                    >
                        <SyncIcon sx={{
                            animation: activeJobs.length > 0 ? 'spin 2s linear infinite' : 'none',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                            }
                        }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        bgcolor: isRetro ? 'rgba(0, 0, 0, 0.95)' : '#fff',
                        border: isRetro ? '1px solid rgba(178, 140, 80, 0.5)' : '1px solid #ddd',
                        borderRadius: isRetro ? '2px' : '8px',
                        minWidth: 300,
                        maxWidth: 380,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        mt: 1,
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    padding: '12px 16px',
                    borderBottom: isRetro ? '1px solid rgba(178, 140, 80, 0.3)' : '1px solid #eee',
                }}>
                    <Typography sx={{
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: isRetro ? '#b28c50' : '#666',
                    }}>
                        Background Tasks
                        {activeJobs.length > 0 && (
                            <Box component="span" sx={{
                                ml: 1,
                                px: 0.75,
                                py: 0.25,
                                bgcolor: isRetro ? 'rgba(178, 140, 80, 0.3)' : 'rgba(255, 152, 0, 0.2)',
                                color: isRetro ? '#b28c50' : '#f57c00',
                                fontSize: '0.65rem',
                                borderRadius: '2px',
                            }}>
                                {activeJobs.length} active
                            </Box>
                        )}
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ padding: '12px 16px', maxHeight: 300, overflowY: 'auto' }}>
                    {/* Active Jobs */}
                    {activeJobs.map((job, idx) => {
                        const StatusIcon = STATUS_ICONS[job.status] || PendingIcon;
                        return (
                            <Box key={job.id || idx} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <StatusIcon sx={{
                                        fontSize: 16,
                                        color: STATUS_COLORS[job.status],
                                        animation: 'spin 2s linear infinite',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' }
                                        }
                                    }} />
                                    <Typography sx={{
                                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                        fontSize: '0.8rem',
                                        color: isRetro ? '#c8c8c8' : '#333',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                    }}>
                                        {job.source_pdf_name}
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    fontSize: '0.7rem',
                                    color: isRetro ? '#888' : '#666',
                                    mb: 0.5,
                                    ml: 3,
                                }}>
                                    {job.status_message}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={job.progress || 0}
                                    sx={{
                                        height: 3,
                                        bgcolor: isRetro ? 'rgba(63, 106, 100, 0.3)' : '#eee',
                                        borderRadius: 1,
                                        ml: 3,
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: STATUS_COLORS[job.status],
                                        }
                                    }}
                                />
                            </Box>
                        );
                    })}

                    {/* Recent completed jobs */}
                    {recentJobs.length > 0 && (
                        <>
                            {activeJobs.length > 0 && (
                                <Typography sx={{
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    fontSize: '0.65rem',
                                    color: isRetro ? '#666' : '#999',
                                    textTransform: 'uppercase',
                                    mt: 2,
                                    mb: 1,
                                }}>
                                    Recent
                                </Typography>
                            )}
                            {recentJobs.map((job, idx) => {
                                const StatusIcon = STATUS_ICONS[job.status] || CompleteIcon;
                                return (
                                    <Box
                                        key={job.id || `recent-${idx}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.75,
                                        }}
                                    >
                                        <StatusIcon sx={{ fontSize: 16, color: STATUS_COLORS[job.status] }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{
                                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                fontSize: '0.8rem',
                                                color: isRetro ? '#c8c8c8' : '#333',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {job.utility_name} {job.tariff_code}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                fontSize: '0.7rem',
                                                color: isRetro ? '#666' : '#999',
                                            }}>
                                                {job.status === 'complete'
                                                    ? `${job.rates_count} rates extracted`
                                                    : 'Failed'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </>
                    )}

                    {/* Empty state */}
                    {activeJobs.length === 0 && recentJobs.length === 0 && (
                        <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            fontSize: '0.8rem',
                            color: isRetro ? '#666' : '#999',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            py: 2,
                        }}>
                            No background tasks
                        </Typography>
                    )}
                </Box>
            </Popover>
        </>
    );
}
